import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";

type Deal = {
	id: string;
	negotiationId: string;
	offerId: string;
	brandOrgId: string;
	affiliateOrgId: string;
	payoutType: "cpa" | "cpl" | "revshare" | "hybrid";
	payoutAmount: number | null;
	payoutPercent: number | null;
	payoutCurrency: string;
	contractWormUri: string;
	contractHash: string;
	state: "active" | "paused" | "completed" | "disputed" | "cancelled";
	createdAt: number;
	updatedAt: number;
};

export function DealDetail() {
	const { id = "" } = useParams();
	const { data, isLoading, error } = useQuery<Deal>({
		queryKey: ["deal", id],
		queryFn: () => api.get(`/deals/${id}`),
		enabled: !!id,
	});

	if (isLoading) return <div className="p-6">Loading…</div>;
	if (error) return <div className="p-6 text-red-700">Error: {(error as Error).message}</div>;
	if (!data) return <div className="p-6 text-neutral-500">Not found</div>;
	const d = data;

	return (
		<div className="space-y-4 p-6">
			<div>
				<Link to="/deals" className="text-sm text-blue-700 hover:underline">
					← Deals
				</Link>
			</div>
			<div>
				<h1 className="text-xl font-semibold">
					{d.payoutType} ·{" "}
					{d.payoutAmount != null
						? `${d.payoutAmount} ${d.payoutCurrency}`
						: `${d.payoutPercent}% ${d.payoutCurrency}`}
				</h1>
				<div className="font-mono text-xs text-neutral-500">{d.id}</div>
			</div>
			<dl className="grid grid-cols-2 gap-2 text-sm">
				<Field label="State" value={d.state} />
				<Field label="Brand org" value={d.brandOrgId} mono />
				<Field label="Affiliate org" value={d.affiliateOrgId} mono />
				<Field label="Negotiation" value={d.negotiationId} mono />
				<Field label="Offer" value={d.offerId} mono />
				<Field label="Contract hash" value={d.contractHash} mono />
				<Field label="Created" value={new Date(d.createdAt * 1000).toISOString().slice(0, 19)} />
			</dl>
			<div className="text-sm">
				<a
					href={`${import.meta.env.VITE_MARKETS_API_URL ?? "https://api.g1.markets"}/deals/${d.id}/contract`}
					target="_blank"
					rel="noreferrer"
					className="text-blue-700 underline"
				>
					View WORM envelope →
				</a>
			</div>
			{d.state === "active" && (
				<div>
					<Link
						to={`/disputes/new?dealId=${d.id}`}
						className="inline-block rounded bg-red-100 px-3 py-1 text-sm text-red-800 hover:bg-red-200"
					>
						Open dispute
					</Link>
				</div>
			)}
		</div>
	);
}

function Field({
	label,
	value,
	mono,
}: {
	label: string;
	value: string | number;
	mono?: boolean;
}) {
	return (
		<div>
			<dt className="text-xs text-neutral-500">{label}</dt>
			<dd className={mono ? "font-mono text-xs text-neutral-900" : "text-neutral-900"}>{value}</dd>
		</div>
	);
}
